import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Eye, ShoppingCart, DollarSign, MousePointer, Play } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [days, setDays] = useState('30');

  useEffect(() => {
    const d = parseInt(days);
    api.get(`/analytics/summary?days=${d}`).then(r => setSummary(r.data)).catch(() => {});
    api.get(`/analytics/revenue-chart?days=${d}`).then(r => setChartData(r.data)).catch(() => {});
  }, [days]);

  const metrics = [
    { label: 'Page Views', value: summary?.page_views || 0, icon: Eye, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Teaser Plays', value: summary?.teaser_plays || 0, icon: Play, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Checkout Starts', value: summary?.checkout_starts || 0, icon: MousePointer, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Purchases', value: summary?.purchases || 0, icon: ShoppingCart, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Revenue', value: `$${(summary?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Conversion', value: summary?.page_views > 0 ? `${((summary?.purchases || 0) / summary.page_views * 100).toFixed(1)}%` : '0%', icon: BarChart3, color: 'text-[#1A9DA5]', bg: 'bg-[#1A9DA5]/10' },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Analytics</h1>
          <p className="text-muted-foreground mt-1">Track your performance</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36" data-testid="analytics-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metrics.map((m, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${m.bg} flex items-center justify-center mb-2`}>
                <m.icon className={`w-4 h-4 ${m.color}`} />
              </div>
              <p className="text-xl font-bold font-['Outfit']">{m.value}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{m.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base font-['Outfit']">Revenue Over Time</CardTitle></CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Bar dataKey="revenue" fill="#1A9DA5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <p>No revenue data for this period. Revenue charts populate after purchases.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
